/**
 * Client-side image downscale utility.
 *
 * Why this exists: Cloudflare's ingress rejects large multipart bodies
 * (~10-25 MB depending on plan) at the edge with an HTTP 520 — the file
 * never touches our backend, so a server-side downscale can't rescue it.
 * The fix is to shrink the photo in the browser first, then upload the
 * lighter blob. Modern camera raws are 20-40 MB → 300-500 KB after this,
 * with zero visible quality loss at 1080-1920 render sizes.
 *
 * Usage:
 *   const smallFile = await downscaleImage(bigFile, { maxEdge: 2400 });
 *   const r = await api.frameStudioUploadPhoto(smallFile);
 *
 * If the input is small enough (or PIL can't decode it — e.g. HEIC on
 * browsers without support), the original File is returned unchanged so
 * the caller doesn't have to special-case anything.
 */
export async function downscaleImage(file, { maxEdge = 2400, quality = 0.85, minBytes = 1_500_000 } = {}) {
  if (!file) return file;
  // Small files: don't waste cycles.
  if (file.size <= minBytes) return file;
  // Only downscale rasters that <img> can decode. SVGs and PDFs pass through.
  if (!/^image\/(jpe?g|png|webp|bmp|heic|heif)/i.test(file.type || '')) return file;

  try {
    // Prefer createImageBitmap — it's off-main-thread on modern browsers
    // and handles EXIF orientation automatically.
    const bmp = 'createImageBitmap' in window
      ? await createImageBitmap(file, { imageOrientation: 'from-image' }).catch(() => null)
      : null;

    let width, height, drawSource;
    if (bmp) {
      width = bmp.width;
      height = bmp.height;
      drawSource = bmp;
    } else {
      // Fallback for browsers without createImageBitmap: use an <img>.
      const url = URL.createObjectURL(file);
      const img = await new Promise((res, rej) => {
        const el = new Image();
        el.onload = () => res(el);
        el.onerror = rej;
        el.src = url;
      });
      URL.revokeObjectURL(url);
      width = img.naturalWidth;
      height = img.naturalHeight;
      drawSource = img;
    }

    // Already small enough on both edges — skip.
    if (Math.max(width, height) <= maxEdge) return file;

    const scale = maxEdge / Math.max(width, height);
    const outW = Math.round(width * scale);
    const outH = Math.round(height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(drawSource, 0, 0, outW, outH);

    const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', quality));
    if (!blob) return file;

    // Preserve original name but force .jpg extension since we re-encoded.
    const base = (file.name || 'photo').replace(/\.[^.]+$/, '');
    return new File([blob], `${base}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
  } catch (e) {
    // Anything goes wrong (CORS, decode error, out-of-memory on a huge
    // photo on a tiny device) — fall through with the original file so
    // the operator can still try to upload it.
    console.warn('downscaleImage: falling back to original file', e);
    return file;
  }
}
